import React, { useEffect, useState } from "react";
import Sidebar from "./sidebar";
import axios from "../../utils/axios";
import toast, { Toaster } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import {
  trackAction,
  collectFeatureChanges,
  collectInspectorText,
  buildClickDetails,
} from "../../utils/clickTracking";
import { getPropertyUpdateErrorMessage } from "../../utils/propertyErrors";
import DuplicatePt5Modal from "../../components/DuplicatePt5Modal";
import { useDuplicatePt5Retry } from "../../hooks/useDuplicatePt5Retry";

const Reassessment = () => {
  const User = JSON.parse(localStorage.getItem("user"));
  const userId = User?.id;

  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [features, setFeatures] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showReassessModal, setShowReassessModal] = useState(false);
  const [showRefModal, setShowRefModal] = useState(false);
  const [showNotReassessModal, setShowNotReassessModal] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [notReassessComment, setNotReassessComment] = useState("");
  const [readyToSubmit, setReadyToSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAreaTable, setShowAreaTable] = useState(false);
  
  // Helper functions for area categories
  const toFiniteNumber = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === "number") return Number.isFinite(val) ? val : null;
    const n = Number(String(val).replaceAll(",", "").trim());
    return Number.isFinite(n) ? n : null;
  };

  const formatTwoDecimals = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value).trim();
    if (!str) return "";
    const lowered = str.toLowerCase();
    if (lowered === "null" || lowered === "undefined") return "";
    const n = toFiniteNumber(str);
    if (n === null) return str;
    return String(Math.round(n));
  };

  const inferLandAreaSqYards = (raw) => {
    const n = toFiniteNumber(raw);
    if (n === null || n < 0) return null;
    const rounded = Math.round(n);
    const isEffectivelyInteger = Math.abs(n - rounded) < 1e-6;
    if (n > 108000) return n / 9;
    if (n > 3000 && isEffectivelyInteger && rounded % 9 === 0) return n / 9;
    return n;
  };

  const inferCoveredAreaSqFt = (raw) => {
    const n = toFiniteNumber(raw);
    if (n === null || n < 0) return null;
    if (n <= 300) return n * 9;
    return n;
  };

  const getLandAreaCategory = (raw) => {
    const yards = inferLandAreaSqYards(raw);
    if (yards === null) return null;
    const cls =
      yards <= 31 ? 1 : yards <= 91 ? 2 : yards <= 175 ? 3 : yards <= 300 ? 4 : 5;
    return { cls };
  };

  const getCoveredAreaCategory = (raw) => {
    const sqft = inferCoveredAreaSqFt(raw);
    if (sqft === null) return null;
    const cls =
      sqft <= 400 ? 1 : sqft <= 977 ? 2 : sqft <= 1710 ? 3 : sqft <= 2920 ? 4 : 5;
    return { cls };
  };
  
  // Explanation state
  const [overallExplanation, setOverallExplanation] = useState("");
  const [rowExplanations, setRowExplanations] = useState({
    floor: "",
    landuse: "",
    landarea: "",
    coveredarea: ""
  });
  const {
    showDuplicatePt5Modal,
    openDuplicatePt5Modal,
    closeDuplicatePt5Modal,
    saveDuplicatePt5Retry,
  } = useDuplicatePt5Retry();

  const applyNewPt5 = (newPt5) => {
    setReferenceNumber(newPt5);
    setFeatures((prev) =>
      prev.map((f) => (f.key === "pt" ? { ...f, after: newPt5 } : f))
    );
  };

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 9;

  const hasUsableValue = (value) => {
    if (value === null || value === undefined) return false;
    const str = String(value).trim().toLowerCase();
    return str !== "" && str !== "null" && str !== "undefined";
  };

  const pickFirstValue = (obj, keys) => {
    for (const key of keys) {
      const value = obj?.[key];
      if (hasUsableValue(value)) return value;
    }
    return "";
  };

  const getMLDisplayValue = (property, field) => {
    let realValue = "";
    switch (field) {
      case 'floor':
        realValue = pickFirstValue(property, ["floors_ml", "floor_ml", "floors_pred", "floor_pred"]);
        break;
      case 'landuse':
        realValue = pickFirstValue(property, ["landuse_ml", "land_use_ml", "land_use_pred", "landuse_pred"]);
        break;
      case 'landarea':
        {
          const ml = pickFirstValue(property, ["landarea_ml", "land_area_ml"]);
          const bucket = pickFirstValue(property, ["landarea_combined_pred"]);
          const n = Number(String(ml ?? "").replaceAll(",", "").trim());
          const mlIsZeroish = Number.isFinite(n) && n <= 0;
          realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
        }
        break;
      case 'coveredarea':
        {
          const ml = pickFirstValue(property, ["coveredarea_ml", "covered_area_ml"]);
          const bucket = pickFirstValue(property, ["coveredarea_combined_pred"]);
          const n = Number(String(ml ?? "").replaceAll(",", "").trim());
          const mlIsZeroish = Number.isFinite(n) && n <= 0;
          realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
        }
        break;
    }

    if (hasUsableValue(realValue)) {
      if (field === "landarea" || field === "coveredarea") {
        const formatted = formatTwoDecimals(realValue);
        return formatted || "N/A";
      }
      const num = Number(realValue);
      if (Number.isFinite(num) && !isNaN(num)) {
        return Number.isInteger(num) ? num : Math.round(num);
      }
      return realValue;
    }
    return 'N/A';
  };

  const parseRange = (rangeStr) => {
    if (!rangeStr) return null;
    const str = String(rangeStr).toLowerCase().trim();
    
    // Remove any units (sq yards, sq yds, sq ft, sq.yds, sq.ft, etc.)
    const cleanStr = str.replace(/sq\.?\s*(yards?|yds?|ft|feet?)|,/g, '').trim();
    
    // Check for "greater than X" or "more than X"
    let match = cleanStr.match(/(?:greater|more)\s+than\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return { type: 'greater', value: Number(match[1]) };
    }
    
    // Check for "less than X" or "under X"
    match = cleanStr.match(/(?:less|under)\s+than\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return { type: 'less', value: Number(match[1]) };
    }
    
    // Check for "between X and Y"
    match = cleanStr.match(/between\s+(\d+(?:\.\d+)?)\s+and\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return { 
        type: 'between', 
        min: Number(match[1]), 
        max: Number(match[2]) 
      };
    }
    
    // Try to parse as a single number
    const num = Number(cleanStr);
    if (Number.isFinite(num)) {
      return { type: 'exact', value: num };
    }
    
    return null;
  };

  const areValuesDifferent = (adminVal, mlVal, field) => {
    const normalizeNumber = (val) => {
      if (val === null || val === undefined || val === 'N/A' || val === '') return null;
      const numStr = String(val).replace(/[^0-9.]/g, '');
      const numeric = Number(numStr);
      if (Number.isFinite(numeric)) return numeric;
      return null;
    };
    
    // For landarea and coveredarea, check if admin is within ML range
    if ((field === "landarea" || field === "land_area" || field === "coveredarea" || field === "covered_area") && mlVal) {
      const range = parseRange(mlVal);
      const adminNum = normalizeNumber(adminVal);
      if (range && typeof adminNum === "number") {
        switch (range.type) {
          case "greater":
            return adminNum <= range.value; // highlight if admin is NOT greater
          case "less":
            return adminNum >= range.value; // highlight if admin is NOT less
          case "between":
            return adminNum < range.min || adminNum > range.max; // highlight if outside range
          case "exact":
            return Math.abs(adminNum - range.value) > 1e-9; // highlight if different
        }
      }
    }

    // For all other fields, use original comparison
    const normalize = (val) => {
      if (val === null || val === undefined || val === 'N/A' || val === '') return '';
      return String(val).trim().toLowerCase();
    };
    const normalizedAdmin = normalize(adminVal);
    const normalizedMl = normalize(mlVal);
    return normalizedAdmin !== '' && normalizedMl !== '' && normalizedAdmin !== normalizedMl;
  };

  const buildFeatures = (p) => [
    { key: "floor", label: "Floor", before: p.floor_admin, after: pickFirstValue(p, ["floors_ml", "floor_ml", "floors_pred", "floor_pred"]) },
    { key: "land_use", label: "Land Use", before: p.landuse_admin, after: pickFirstValue(p, ["landuse_ml", "land_use_ml", "land_use_pred", "landuse_pred"]) },
    { key: "land_area", label: "Land Area", before: p.landarea_admin, after: (() => {
      const ml = pickFirstValue(p, ["landarea_ml", "land_area_ml"]);
      const bucket = pickFirstValue(p, ["landarea_combined_pred"]);
      const n = Number(String(ml ?? "").replaceAll(",", "").trim());
      const mlIsZeroish = Number.isFinite(n) && n <= 0;
      return hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
    })() },
    { key: "covered_area", label: "Covered Area", before: p.coveredarea_admin, after: (() => {
      const ml = pickFirstValue(p, ["coveredarea_ml", "covered_area_ml"]);
      const bucket = pickFirstValue(p, ["coveredarea_combined_pred"]);
      const n = Number(String(ml ?? "").replaceAll(",", "").trim());
      const mlIsZeroish = Number.isFinite(n) && n <= 0;
      return hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
    })() },
    { key: "pt", label: "PT-5 Serial", before: "", after: pickFirstValue(p, ["refno", "pt5", "pt_5", "pt"]) },
  ];

  const loadProperties = async () => {
    try {
      const { data } = await axios.get("/inspector/properties");
      setProperties(data);
    } catch {
      toast.error("Failed to load properties.");
    }
  };

  useEffect(() => {
    loadProperties();
  }, []);

  const handleOpenDetails = (p) => {
    setSelectedProperty(p);
    setFeatures(buildFeatures(p));
    setShowDetailModal(true);
    // Reset explanation state
    setOverallExplanation("");
    setRowExplanations({
      floor: "",
      landuse: "",
      landarea: "",
      coveredarea: ""
    });
  };

  const updateFeature = (i, field, value) => {
    const updated = [...features];
    const f = updated[i];
    
    // Validate based on field type
    let validatedValue = value;
    if (field === "after") {
      // Check if new value is same as old value
      const oldValStr = String(f.before || "").trim().toLowerCase();
      const newValStr = String(validatedValue || "").trim().toLowerCase();
      if (oldValStr !== "" && newValStr !== "" && oldValStr === newValStr) {
        toast.error("New value must be different from current value!");
        return;
      }
      
      switch (f.key) {
        case "floor":
        case "land_area":
        case "covered_area":
          // Should be a number (allow only digits, optional decimal)
          if (/[^0-9.]/.test(value)) {
            toast.error("Please enter only numbers for this field!");
            validatedValue = value.replace(/[^0-9.]/g, "");
          }
          // Prevent multiple decimals
          if ((validatedValue.match(/\./g) || []).length > 1) {
            validatedValue = validatedValue.slice(0, -1);
          }
          // Validate area ranges
          if (validatedValue) {
            const numVal = parseFloat(validatedValue);
            if (f.key === "land_area") {
              // Land Area (sq yards) ranges: 0-31, 32-91, 92-175, 176-300, 301-108000
              if (numVal < 0 || numVal > 108000) {
                toast.error("Land Area must be between 0 and 108,000 sq yards!");
              }
            } else if (f.key === "covered_area") {
              // Covered Area (sq ft) ranges: 0-400, 401-977, 978-1710, 1711-2920, 2921-272000
              if (numVal < 0 || numVal > 272000) {
                toast.error("Covered Area must be between 0 and 272,000 sq ft!");
              }
            }
          }
          break;
        case "land_use":
          // Should be text only (no numbers)
          if (/[0-9]/.test(value)) {
            toast.error("Please enter only letters for Land Use!");
            validatedValue = value.replace(/[0-9]/g, "");
          }
          break;
      }
    }
    
    updated[i][field] = validatedValue;
    setFeatures(updated);
  };
  
  const updateRowExplanation = (key, value) => {
    setRowExplanations(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Do Not Reassess
  const handleNotReassess = async () => {
    if (!selectedProperty) return;
    try {
      setSubmitting(true);
      const trimmed = String(notReassessComment ?? "").trim();
      const fd = new FormData();
      fd.append("pin", selectedProperty.pin);
      fd.append("userId", userId);
      fd.append("reassess", "0");
      fd.append("comment", trimmed);

      ["circle","occupationstatus","location","floor_admin","landuse_admin","landarea_admin","coveredarea_admin","admin_garv"].forEach(field => {
        fd.append(field, selectedProperty[field] || "");
      });
      
      // Preserve reference number
      const refVal = referenceNumber || selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt || "";
      if (refVal) {
        fd.append("refno", refVal);
      }

      if (trimmed && userId) {
        try {
          await axios.post("/inspector/save-explanation", {
            property_id: selectedProperty.id,
            user_id: userId,
            overall_explanation: trimmed,
            row_explanations: {
              floor: "",
              landuse: "",
              landarea: "",
              coveredarea: "",
            },
          });
        } catch (err) {
          console.error("Failed to save explanation:", err);
        }
      }

      await axios.put(`/properties/${selectedProperty.pin}`, fd);

      await trackAction(
        "do_not_reassess",
        selectedProperty.pin,
        buildClickDetails({
          inspectorText: trimmed
            ? `Reason for not reassessing: ${trimmed}`
            : "No reason provided",
        }),
        {
          comment_text: trimmed
            ? `Reason for not reassessing: ${trimmed}`
            : "No reason provided",
        }
      );

      toast.success("Marked as 'Do Not Reassess'");
      setShowNotReassessModal(false);
      setShowDetailModal(false);
      loadProperties();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    } finally {
      setSubmitting(false);
    }
  };

  // Submit Reassess
  const handleSubmitReassess = async ({ featuresOverride, refnoOverride } = {}) => {
    if (!selectedProperty) return;
    const activeFeatures = featuresOverride ?? features;

    try {
      setSubmitting(true);
      
      // First, save the explanation
      await axios.post("/inspector/save-explanation", {
        property_id: selectedProperty.id,
        user_id: userId,
        overall_explanation: overallExplanation,
        row_explanations: rowExplanations
      });

      // Then, save the reassessment
      const fd = new FormData();
      fd.append("pin", selectedProperty.pin);
      fd.append("userId", userId);
      fd.append("reassess", "1");

      // Existing admin values
      fd.append("circle", selectedProperty.circle || "");
      fd.append("occupationstatus", selectedProperty.occupationstatus || "");
      fd.append("location", selectedProperty.location || "");
      fd.append("floor_admin", selectedProperty.floor_admin || "");
      fd.append("landuse_admin", selectedProperty.landuse_admin || "");
      fd.append("landarea_admin", selectedProperty.landarea_admin || "");
      fd.append("coveredarea_admin", selectedProperty.coveredarea_admin || "");
      fd.append("admin_garv", selectedProperty.admin_garv || "");

      // Updated prediction values
      activeFeatures.forEach(f => {
        const val = f.key === "pt" && refnoOverride
          ? refnoOverride
          : f.after?.toString().trim();
        if (!val) return;
        switch (f.key) {
          case "floor": fd.append("floors_pred", val); break;
          case "land_use": fd.append("land_use_pred", val); break;
          case "land_area": fd.append("landarea_combined_pred", val); break;
          case "covered_area": fd.append("coveredarea_combined_pred", val); break;
          case "pt": fd.append("refno", val); break;
        }
      });
      
      // Preserve existing refno if not updated
      const existingRef = refnoOverride || referenceNumber || selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt || "";
      if (existingRef && !activeFeatures.some(f => f.key === "pt" && f.after) && !refnoOverride) {
        fd.append("refno", existingRef);
      }

      await axios.put(`/properties/${selectedProperty.pin}`, fd);

      const featureChanges = collectFeatureChanges(activeFeatures);
      await trackAction(
        "reassess",
        selectedProperty.pin,
        buildClickDetails({
          featureChanges: featureChanges.length ? featureChanges : ["No feature changes"],
          inspectorText: collectInspectorText({ overallExplanation, rowExplanations, features: activeFeatures }),
        })
      );

      toast.success("Reassessment and explanation saved!");
      setShowReassessModal(false);
      setShowRefModal(false);
      setShowDetailModal(false);
      setSelectedProperty(null);
      setReadyToSubmit(false);
      loadProperties();
    } catch (err) {
      console.error(err);
      if (openDuplicatePt5Modal(err, async (newPt5) => {
        const updatedFeatures = activeFeatures.map((f) =>
          f.key === "pt" ? { ...f, after: newPt5 } : f
        );
        applyNewPt5(newPt5);
        await handleSubmitReassess({
          featuresOverride: updatedFeatures,
          refnoOverride: newPt5,
        });
      })) {
        return;
      }
      toast.error(getPropertyUpdateErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Auto submit after reference number is set
  useEffect(() => {
    if (readyToSubmit && selectedProperty) handleSubmitReassess();
  }, [readyToSubmit]);

  const paginatedProperties = properties.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const totalPages = Math.ceil(properties.length / pageSize);

  const isReassessValid = 
    features.filter(f => f.key !== "pt").some(f => f.after?.toString().trim() !== "") && 
    overallExplanation.trim() !== "";

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Sidebar />
      <Toaster position="top-right" />

      <div className="flex-1 p-8 overflow-y-auto">
        <h1 className="text-3xl font-bold mb-6">
          Properties <span className="text-blue-600">Reassessment</span>
        </h1>

        {/* PROPERTY GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedProperties.map(p => (
            <div
              key={p.id}
              className="bg-white p-4 rounded-xl shadow hover:shadow-lg cursor-pointer transition transform hover:-translate-y-1"
              onClick={() => handleOpenDetails(p)}
              data-click-id="view_properties_view_details"
              data-property-id={p.pin}
            >
              <h3 className="font-bold truncate text-lg">{p.pin}</h3>
              <p className="text-sm text-gray-600">{p.location}</p>
              <p className="text-sm mt-1">
                <strong>Circle:</strong> {p.circle}
              </p>
            </div>
          ))}
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6 gap-2">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                className={`px-3 py-1 rounded-lg border ${currentPage === i + 1 ? "bg-blue-600 text-white" : "bg-white"}`}
                onClick={() => setCurrentPage(i + 1)}
                data-click-id={i + 1 === currentPage ? "view_properties_page_prev" : "view_properties_page_next"}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}

        {/* DETAIL MODAL */}
        <AnimatePresence>
          {showDetailModal && selectedProperty && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex justify-center items-center z-50"
            >
              <motion.div initial={{ scale: 0.85 }} animate={{ scale: 1 }} exit={{ scale: 0.85 }}
                className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-2xl overflow-y-auto max-h-[90vh]"
              >
                <div className="flex justify-between items-center mb-4 border-b pb-2">
                  <div className="flex items-center">
                    <h2 className="text-2xl font-bold text-gray-800">Property Information</h2>
                    <div className="flex items-center ml-4">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-red-100 border border-red-200 rounded mr-1.5"></div>
                        <span className="text-xs text-gray-500">Difference Flagged</span>
                      </div>
                    </div>
                  </div>
                  <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600 transition" data-click-id="view_properties_close_details">✕</button>
                </div>

                <div className="overflow-x-auto mb-6">
                  <table className="w-full border rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-gray-100 text-sm">
                        <th className="p-3 text-left font-semibold text-gray-700">Feature</th>
                        <th className="p-3 text-left font-semibold text-gray-700">Admin</th>
                        <th className="p-3 text-left font-semibold text-gray-700">ML (Computer Vision Prediction)</th>
                        <th className="p-3 text-left font-semibold text-gray-700">Reassessment</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm">
                      {[
                        { 
                          label: "Floor", 
                          field: "floor",
                          admin: selectedProperty.floor_admin, 
                          ml: getMLDisplayValue(selectedProperty, 'floor'),
                          reassess: selectedProperty.floors_pred
                        },
                        { 
                          label: "Land Use", 
                          field: "landuse",
                          admin: selectedProperty.landuse_admin, 
                          ml: getMLDisplayValue(selectedProperty, 'landuse'),
                          reassess: selectedProperty.land_use_pred
                        },
                        { 
                          label: "Land Area", 
                          field: "landarea",
                          admin: selectedProperty.landarea_admin, 
                          ml: getMLDisplayValue(selectedProperty, 'landarea'),
                          reassess: selectedProperty.landarea_combined_pred,
                          unit: "sq.yards"
                        },
                        { 
                          label: "Covered Area", 
                          field: "coveredarea",
                          admin: selectedProperty.coveredarea_admin, 
                          ml: getMLDisplayValue(selectedProperty, 'coveredarea'),
                          reassess: selectedProperty.coveredarea_combined_pred,
                          unit: "sq.ft"
                        }
                      ].map((row) => {
                        const isDifferent = areValuesDifferent(row.admin, row.ml, row.field);
                        return (
                          <tr key={row.label} className={`border-b last:border-0 ${isDifferent ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                            <td className="p-3 font-medium text-gray-800">{row.label}</td>
                            <td className="p-3 text-gray-600">
                              {row.label === "Land Area" || row.label === "Covered Area"
                                ? (row.admin
                                    ? `${formatTwoDecimals(row.admin) || row.admin} ${row.unit}`
                                    : "N/A")
                                : (row.admin || "N/A")}
                            </td>
                            <td className={`p-3 font-medium ${isDifferent ? 'text-red-600' : 'text-gray-600'}`}>
                              {row.ml}
                            </td>
                            <td className="p-3 font-semibold text-blue-700">
                              {row.reassess
                                ? (row.unit
                                    ? `${formatTwoDecimals(row.reassess) || row.reassess} ${row.unit}`
                                    : (formatTwoDecimals(row.reassess) || row.reassess))
                                : "N/A"}
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="bg-blue-50/30">
                        <td className="p-3 font-medium text-gray-800">PT-5 Serial</td>
                        <td className="p-3 text-gray-600">—</td>
                        <td className="p-3 text-gray-600">—</td>
                        <td className="p-3 font-semibold text-blue-700">{selectedProperty.refno || "N/A"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end mt-4 gap-3">
                  {(() => {
                    const hasInspectorDecision = 
                      (selectedProperty.reassess === 0 || selectedProperty.reassess === 1 || 
                       selectedProperty.reassess === "0" || selectedProperty.reassess === "1" ||
                       selectedProperty.reassess === true || selectedProperty.reassess === false);
                    const isSupervisorDecisionMade = selectedProperty.status === "resolved";
                    
                    if (isSupervisorDecisionMade) {
                      return (
                        <div className="text-center py-4">
                          <p className="text-gray-500">Supervisor has already made a decision.</p>
                          <p className="text-sm text-gray-400">Cannot edit final decision.</p>
                        </div>
                      );
                    }
                    
                    if (hasInspectorDecision) {
                      return (
                        <>
                          <button onClick={() => {
                            setReferenceNumber(selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt || "");
                            setShowReassessModal(true);
                          }}
                            className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 transition"
                            data-click-id="view_properties_edit_reassess"
                            data-property-id={selectedProperty.pin}>
                            Edit: Reassess
                          </button>
                          <button onClick={() => {
                            setReferenceNumber(selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt || "");
                            setShowNotReassessModal(true);
                          }}
                            className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition"
                            data-click-id="view_properties_change_to_do_not_reassess"
                            data-property-id={selectedProperty.pin}>
                            Edit: Do Not Reassess
                          </button>
                        </>
                      );
                    }
                    
                    return (
                      <>
                        <button onClick={() => {
                            setReferenceNumber(selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt || "");
                            setShowReassessModal(true);
                          }}
                          className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 transition"
                          data-click-id="reassess"
                          data-property-id={selectedProperty.pin}>
                          Reassess
                        </button>
                        <button onClick={() => {
                            setReferenceNumber(selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt || "");
                            setShowNotReassessModal(true);
                          }}
                          className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition"
                          data-click-id="do_not_reassess"
                          data-property-id={selectedProperty.pin}>
                          Do Not Reassess
                        </button>
                      </>
                    );
                  })()}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* REASSESS MODAL */}
        <AnimatePresence>
          {showReassessModal && selectedProperty && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex justify-center items-start md:items-center overflow-auto z-50 p-4"
            >
              <motion.div initial={{ scale: 0.85 }} animate={{ scale: 1 }} exit={{ scale: 0.85 }}
                className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-4xl overflow-y-auto max-h-[95vh]"
              >
                <h2 className="text-2xl font-bold mb-4">Reassessment Form</h2>

                <div className="space-y-4 mb-4">
                  {features.filter(f=>f.key!=="pt").map((f,i)=>(
                    <div key={i} className="flex flex-col md:flex-row gap-3 items-start">
                      <div className="w-full md:w-32">
                        <div className="bg-gray-100 border border-gray-200 p-3 rounded text-left font-medium text-gray-800">
                          {f.label}
                        </div>
                      </div>
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <input 
                            className="w-full border border-gray-300 p-3 rounded bg-gray-50 text-gray-700" 
                            value={f.before} 
                            readOnly 
                          />
                        </div>
                        <div>
                          {(f.key === "landarea" || f.key === "land_area" || f.key === "coveredarea" || f.key === "covered_area") ? (
                            <div>&nbsp;</div>
                          ) : (
                            <input 
                              className="w-full border border-gray-300 p-3 rounded"
                              placeholder={`Enter ${f.label.toLowerCase()}...`}
                              value={f.after} 
                              onChange={e=>updateFeature(i,"after",e.target.value)}
                            />
                          )}
                        </div>
                        <div>
                          {(f.key === "landarea" || f.key === "land_area" || f.key === "coveredarea" || f.key === "covered_area") ? (
                            <div>&nbsp;</div>
                          ) : (
                            <input
                              className="w-full border border-gray-300 p-3 rounded"
                              placeholder={`Explain ${f.label.toLowerCase()} change...`}
                              value={rowExplanations[f.key.replace("_", "")] || ""}
                              onChange={e => updateRowExplanation(f.key.replace("_", ""), e.target.value)}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Overall Explanation */}
                <div className="mb-4">
                  <label className="block font-medium text-gray-700 mb-2">Overall Explanation</label>
                  <textarea 
                    className="w-full border p-3 rounded-lg"
                    rows={4}
                    placeholder="Provide an overall explanation for the reassessment..."
                    value={overallExplanation}
                    onChange={e => setOverallExplanation(e.target.value)}
                  />
                </div>

                <div className="flex justify-end gap-3 mt-4">
                  <button
                    onClick={()=>{
                      setReferenceNumber("");
                      setShowReassessModal(false);
                      setShowRefModal(true);
                    }}
                    disabled={!isReassessValid}
                    className={`px-4 py-2 rounded text-white ${isReassessValid?"bg-green-600 hover:bg-green-700":"bg-blue-300 cursor-not-allowed"}`}
                    data-property-id={selectedProperty.pin}>
                    Next
                  </button>
                  <button onClick={()=>setShowReassessModal(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300" data-click-id="cancel">Cancel</button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* REFERENCE MODAL */}
        <AnimatePresence>
          {showRefModal && selectedProperty && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            >
              <motion.div initial={{ scale: 0.85 }} animate={{ scale: 1 }} exit={{ scale: 0.85 }}
                className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl"
              >
                <h2 className="text-xl font-bold mb-4">
                  {selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt 
                    ? "Edit PT-5 Serial Number" 
                    : "Enter PT-5 Serial Number"}
                </h2>

                <input type="text" value={referenceNumber} onChange={e=>setReferenceNumber(e.target.value)}
                  placeholder="PT-5 Serial Number" className="w-full p-3 border rounded mb-2" />
                {!referenceNumber && <p className="text-red-500 text-sm mb-3">Reference number is required</p>}

                <div className="flex gap-3 mt-4">
                  <button onClick={()=>{
                    setFeatures(prev=>prev.map(f=>f.key==="pt"?{...f,after:referenceNumber}:f));
                    setShowRefModal(false);
                    setReadyToSubmit(true);
                  }} disabled={!referenceNumber || submitting}
                    className={`flex-1 px-4 py-2 rounded text-white ${referenceNumber?"bg-blue-600 hover:bg-blue-700":"bg-gray-400 cursor-not-allowed"}`}
                    data-property-id={selectedProperty.pin}>
                    {submitting?"Saving...":(selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt ? "Update" : "Save")}
                  </button>
                  <button onClick={()=>setShowRefModal(false)} className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded" data-click-id="pt5_cancel">Cancel</button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* DO NOT REASSESS MODAL */}
        <AnimatePresence>
          {showNotReassessModal && selectedProperty && (
            <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <motion.div initial={{scale:0.85}} animate={{scale:1}} exit={{scale:0.85}}
                className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl">
                <h2 className="text-xl font-bold mb-4 text-gray-800">Reason for Not Reassessing</h2>
                
                <div className="mb-4">
                  <label className="block font-medium text-gray-700 mb-2">
                    {selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt 
                      ? "Edit PT-5 Serial Number" 
                      : "PT-5 Serial Number"}
                  </label>
                  <input 
                    type="text" 
                    value={referenceNumber} 
                    onChange={e=>setReferenceNumber(e.target.value)}
                    placeholder="PT-5 Serial Number" 
                    className="w-full p-3 border rounded" 
                  />
                </div>
                
                <textarea 
                  value={notReassessComment} 
                  onChange={e=>setNotReassessComment(e.target.value)}
                  rows={4} 
                  placeholder="Enter reason / comments..." 
                  className="w-full p-3 border rounded mb-3" 
                />
                {!notReassessComment && <p className="text-red-500 text-sm mb-3">Comment is required</p>}

                <div className="flex gap-3">
                  <button onClick={handleNotReassess} disabled={!notReassessComment || submitting}
                    className={`flex-1 px-4 py-2 rounded text-white ${notReassessComment?"bg-gray-700 hover:bg-gray-800":"bg-gray-400 cursor-not-allowed"}`}
                    data-click-id="reason_for_not_reassessing_confirm"
                    data-property-id={selectedProperty.pin}>
                    {submitting?"Saving...":(selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt ? "Update" : "Confirm")}
                  </button>
                  <button onClick={()=>setShowNotReassessModal(false)} className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded" data-click-id="reason_for_not_reassessing_cancel">Cancel</button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <DuplicatePt5Modal
          isOpen={showDuplicatePt5Modal}
          onClose={closeDuplicatePt5Modal}
          onSave={saveDuplicatePt5Retry}
          submitting={submitting}
        />
      </div>
    </div>
  );
};

export default Reassessment;
